import { createBaseCommitmentRoutes } from "features/commitments/CommitmentRoutes";
import { getCapitalActivityTableDataRetrieveLoader } from "gen/endpoints/capital-activity/capital-activity";
import { getCommitmentInformationTableDataByFundRetrieveLoader } from "gen/endpoints/commitment-information-table-data-by-fund/commitment-information-table-data-by-fund";
import { getCommitmentStatsRetrieveLoader } from "gen/endpoints/commitment-stats/commitment-stats";
import { getFeesAndCarryRetrieveLoader } from "gen/endpoints/fees-and-carry/fees-and-carry";
import { getFundListLoader } from "gen/endpoints/fund-entities/fund-entities";
import { getInvestorEntityRetrieveLoader } from "gen/endpoints/investor-entity/investor-entity";
import i18n from "i18n/config";
import { RouteObject } from "react-router-dom";
import {
  composeLoaders,
  removeRouteDescriptions,
} from "shared/utils/RoutingUtils";

export type BaseInvestorEntityRoutesParentId =
  | "fundFamilyConfigurationInvestorEntities"
  | "fundFamilyCommitmentsInvestorEntities"
  | "investorsInvestorEntities"
  | "investorEntities";

export const createBaseInvestorEntityRoutes = (
  idPrefix: BaseInvestorEntityRoutesParentId,
) => {
  const includeDescriptions =
    idPrefix === "fundFamilyConfigurationInvestorEntities";
  const routesWithDescriptions = [
    {
      id: `${idPrefix}InvestorEntity`,
      path: ":investorEntityId",
      lazy: async () => {
        const { InvestorEntityPage } = await import(
          "features/investor-entities/InvestorEntityPage"
        );
        return { Component: InvestorEntityPage };
      },
      loader: composeLoaders(
        getInvestorEntityRetrieveLoader(({ investorEntityId }) => ({
          id: investorEntityId!,
        })),
        getFundListLoader(({ investorEntityId }) => ({
          investor_entity_id: investorEntityId!,
        })),
        getFeesAndCarryRetrieveLoader(({ investorEntityId }) => ({
          investor_entity_id: investorEntityId!,
        })),
        getCommitmentStatsRetrieveLoader(({ investorEntityId }) => ({
          investor_entity_id: investorEntityId!,
        })),
      ),
      children: [
        {
          id: `${idPrefix}InvestorEntityTabActivity`,
          path: "activity",
          lazy: async () => {
            const { InvestorEntityTabActivity } = await import(
              "features/investor-entities/tabs/InvestorEntityTabActivity"
            );
            return { Component: InvestorEntityTabActivity };
          },
          loader: composeLoaders(
            getCommitmentStatsRetrieveLoader(({ investorEntityId }) => ({
              investor_entity_id: investorEntityId!,
            })),
            getCapitalActivityTableDataRetrieveLoader(
              ({ investorEntityId }) => ({
                investor_entity_id: investorEntityId!,
              }),
            ),
          ),
          handle: {
            title: i18n.t("investorEntity:tabs.activity.title"),
            description:
              "Investor Entity Page - Capital Activity Section. View capital activity for the investor entity.",
          },
        },
        {
          id: `${idPrefix}InvestorEntityTabProfile`,
          path: "profile",
          lazy: async () => {
            const { InvestorEntityTabProfile } = await import(
              "features/investor-entities/tabs/profile/InvestorEntityTabProfile"
            );
            return { Component: InvestorEntityTabProfile };
          },
          handle: {
            title: i18n.t("investorEntity:tabs.profile.title"),
            description:
              "Investor Entity Page - Profile Section. View profile information for the investor entity.",
          },
        },
        {
          id: `${idPrefix}InvestorEntityTabCommitments`,
          path: "commitments",
          lazy: async () => {
            const { InvestorEntityTabCommitments } = await import(
              "features/investor-entities/tabs/InvestorEntityTabCommitments"
            );
            return { Component: InvestorEntityTabCommitments };
          },
          loader: getCommitmentInformationTableDataByFundRetrieveLoader(
            ({ investorEntityId }) => ({
              investor_entity_id: investorEntityId!,
            }),
          ),
          handle: {
            title: i18n.t("investorEntity:tabs.commitments.title"),
            description:
              "Investor Entity Page - Commitments Section. View the investor entity's commitments.",
          },
        },
        {
          id: `${idPrefix}InvestorEntityTabPerformance`,
          path: "performance",
          lazy: async () => {
            const { HypotheticalPerformanceTab } = await import(
              "features/financial-reporting/HypotheticalPerformanceTab"
            );
            return { Component: HypotheticalPerformanceTab };
          },
          handle: {
            title: i18n.t("investorEntity:tabs.performance.title"),
            description:
              "Investor Entity Page - Performance Section. View performance information for the investor entity.",
          },
        },
      ],
    },
  ];
  const cleanedRoutes = includeDescriptions
    ? routesWithDescriptions
    : removeRouteDescriptions(routesWithDescriptions);
  return cleanedRoutes;
};

type InvestorEntityRoutesParentId =
  | "fundFamilyConfiguration"
  | "fundFamilyCommitments"
  | "investors";

export const createInvestorEntityRoutes = (
  parentId?: InvestorEntityRoutesParentId,
): RouteObject => {
  const idPrefix: BaseInvestorEntityRoutesParentId = parentId
    ? `${parentId}InvestorEntities`
    : "investorEntities";
  return {
    id: idPrefix,
    path: "investor-entities",
    children: [
      ...createBaseInvestorEntityRoutes(idPrefix),
      {
        id: `${idPrefix}Commitments`,
        path: ":investorEntityId/commitments",
        children: createBaseCommitmentRoutes(`${idPrefix}Commitments`),
      },
    ],
  };
};
